/**
 * Cookie Preference Manager
 * Integrates with Shopify Customer Privacy API
 */
(function () {
  'use strict';

  class CookiePreferenceManager {
    constructor() {
      this.modal = null;
      this.overlay = null;
      this.toast = null;
      this.previouslyFocusedElement = null;
      this.focusableElements = [];
      this.isOpen = false;
      this.consentCategories = ['analytics', 'marketing', 'preferences', 'sale_of_data'];
    }

    init() {
      this.modal = document.getElementById('cp-modal');
      this.overlay = document.getElementById('cp-overlay');
      this.toast = document.getElementById('cp-toast');

      if (!this.modal || !this.overlay) return;

      this.bindEvents();
    }

    bindEvents() {
      // Footer link triggers - use event delegation for reliability
      document.body.addEventListener('click', (e) => {
        const trigger = e.target.closest('[data-cookie-preferences-trigger]');
        if (trigger) {
          e.preventDefault();
          e.stopPropagation();
          this.openModal();
        }
      });

      // Close button
      const closeBtn = this.modal.querySelector('[data-cp-close]');
      if (closeBtn) {
        closeBtn.addEventListener('click', () => this.closeModal());
      }

      // Overlay click to close
      this.overlay.addEventListener('click', (e) => {
        if (e.target === this.overlay) {
          this.closeModal();
        }
      });

      // Keyboard events
      document.addEventListener('keydown', (e) => {
        if (!this.isOpen) return;
        if (e.key === 'Escape') {
          this.closeModal();
        }
        if (e.key === 'Tab') {
          this.trapFocus(e);
        }
      });

      // Accept all
      const acceptAllBtn = this.modal.querySelector('[data-cp-accept-all]');
      if (acceptAllBtn) {
        acceptAllBtn.addEventListener('click', () => this.acceptAll());
      }

      // Decline all
      const declineAllBtn = this.modal.querySelector('[data-cp-decline-all]');
      if (declineAllBtn) {
        declineAllBtn.addEventListener('click', () => this.declineAll());
      }

      // Save preferences
      const saveBtn = this.modal.querySelector('[data-cp-save]');
      if (saveBtn) {
        saveBtn.addEventListener('click', () => this.saveConsent());
      }
    }

    openModal() {
      this.previouslyFocusedElement = document.activeElement;
      this.loadConsent();
      this.overlay.classList.add('cp-overlay--active');
      this.overlay.setAttribute('aria-hidden', 'false');
      document.body.classList.add('cp-modal-open');
      this.isOpen = true;

      // Set focusable elements
      this.focusableElements = Array.from(
        this.modal.querySelectorAll(
          'button, [href], input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])'
        )
      );

      // Focus first focusable element
      if (this.focusableElements.length > 0) {
        setTimeout(() => this.focusableElements[0].focus(), 100);
      }
    }

    closeModal() {
      this.overlay.classList.remove('cp-overlay--active');
      this.overlay.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('cp-modal-open');
      this.isOpen = false;

      // Restore focus
      if (this.previouslyFocusedElement) {
        this.previouslyFocusedElement.focus();
      }
    }

    trapFocus(e) {
      if (this.focusableElements.length === 0) return;

      const firstEl = this.focusableElements[0];
      const lastEl = this.focusableElements[this.focusableElements.length - 1];

      if (e.shiftKey) {
        if (document.activeElement === firstEl) {
          e.preventDefault();
          lastEl.focus();
        }
      } else {
        if (document.activeElement === lastEl) {
          e.preventDefault();
          firstEl.focus();
        }
      }
    }

    loadConsent() {
      try {
        if (
          window.Shopify &&
          window.Shopify.customerPrivacy &&
          typeof window.Shopify.customerPrivacy.currentVisitorConsent === 'function'
        ) {
          const consent = window.Shopify.customerPrivacy.currentVisitorConsent();
          this.updateCheckboxes(consent);
        } else {
          // Fallback: try reading from localStorage
          const stored = localStorage.getItem('cp_consent_preferences');
          if (stored) {
            try {
              const parsed = JSON.parse(stored);
              this.updateCheckboxes(parsed);
            } catch (e) {
              // Invalid stored data, use defaults
            }
          }
        }
      } catch (error) {
        console.warn('Cookie Preferences: Unable to load consent status.', error);
      }
    }

    updateCheckboxes(consent) {
      if (!consent) return;

      const mapping = {
        analytics: 'cp-analytics',
        marketing: 'cp-marketing',
        preferences: 'cp-personalization',
        sale_of_data: 'cp-sale-of-data',
      };

      Object.keys(mapping).forEach((key) => {
        const checkbox = document.getElementById(mapping[key]);
        if (checkbox && consent[key] !== undefined) {
          // Shopify returns 'yes', 'no', or '' for consent values
          checkbox.checked = consent[key] === 'yes' || consent[key] === true;
        }
      });
    }

    getCheckboxValues() {
      const marketing = document.getElementById('cp-marketing')?.checked || false;
      const saleOfDataEl = document.getElementById('cp-sale-of-data');
      // If sale_of_data checkbox exists use its value, otherwise mirror marketing consent
      const saleOfData = saleOfDataEl ? saleOfDataEl.checked : marketing;
      return {
        analytics: document.getElementById('cp-analytics')?.checked || false,
        marketing: marketing,
        preferences: document.getElementById('cp-personalization')?.checked || false,
        sale_of_data: saleOfData,
      };
    }

    async acceptAll() {
      // Check all checkboxes
      const checkboxes = this.modal.querySelectorAll('input[type="checkbox"]:not([disabled])');
      checkboxes.forEach((cb) => (cb.checked = true));
      await this.saveConsent();
    }

    async declineAll() {
      // Uncheck all except required
      const checkboxes = this.modal.querySelectorAll('input[type="checkbox"]:not([disabled])');
      checkboxes.forEach((cb) => (cb.checked = false));
      await this.saveConsent();
    }

    async saveConsent() {
      const saveBtn = this.modal.querySelector('[data-cp-save]');
      const acceptBtn = this.modal.querySelector('[data-cp-accept-all]');
      const declineBtn = this.modal.querySelector('[data-cp-decline-all]');

      // Loading state
      [saveBtn, acceptBtn, declineBtn].forEach((btn) => {
        if (btn) btn.classList.add('cp-btn--loading');
      });

      const values = this.getCheckboxValues();

      try {
        if (
          window.Shopify &&
          window.Shopify.customerPrivacy &&
          typeof window.Shopify.customerPrivacy.setTrackingConsent === 'function'
        ) {
          await window.Shopify.customerPrivacy.setTrackingConsent(
            {
              analytics: values.analytics,
              marketing: values.marketing,
              preferences: values.preferences,
              sale_of_data: values.sale_of_data,
            },
            () => {}
          );
        }

        // Store locally as fallback and for timestamp
        const storageData = {
          ...values,
          timestamp: new Date().toISOString(),
        };
        localStorage.setItem('cp_consent_preferences', JSON.stringify(storageData));

        // Dispatch custom event
        document.dispatchEvent(new CustomEvent('cookiePreferencesUpdated', { detail: values }));

        // Close and show success
        this.closeModal();
        this.showToast();
      } catch (error) {
        console.error('Cookie Preferences: Failed to save consent.', error);
        // Still close and store locally
        const storageData = {
          ...values,
          timestamp: new Date().toISOString(),
        };
        localStorage.setItem('cp_consent_preferences', JSON.stringify(storageData));
        document.dispatchEvent(new CustomEvent('cookiePreferencesUpdated', { detail: values }));
        this.closeModal();
        this.showToast();
      } finally {
        [saveBtn, acceptBtn, declineBtn].forEach((btn) => {
          if (btn) btn.classList.remove('cp-btn--loading');
        });
      }
    }

    showToast() {
      if (!this.toast) return;
      this.toast.classList.add('cp-toast--visible');
      setTimeout(() => {
        this.toast.classList.remove('cp-toast--visible');
      }, 3000);
    }
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      const manager = new CookiePreferenceManager();
      manager.init();
    });
  } else {
    const manager = new CookiePreferenceManager();
    manager.init();
  }
})();
